(function () {
  // One queue for all matrix section reads, including UI hydration and ATC.
  // The limit is shared across colours, not multiplied by the number of panels.
  function createReadQueue(limit) {
    var active = 0;
    var pending = [];
    function pump() {
      while (active < limit && pending.length) {
        var job = pending.shift();
        active++;
        (function (current) {
          Promise.resolve().then(current.run).then(current.resolve, current.reject)
            .then(function () { active--; pump(); });
        })(job);
      }
    }
    return function (run) {
      return new Promise(function (resolve, reject) {
        pending.push({ run: run, resolve: resolve, reject: reject });
        pump();
      });
    };
  }
  var enqueueSectionRead = createReadQueue(3);

  function readSectionText(url, fresh) {
    return enqueueSectionRead(function () {
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = controller ? setTimeout(function () { controller.abort(); }, 15000) : null;
      var request = Promise.resolve().then(function () {
        return fetch(url, {
          credentials: 'same-origin', cache: fresh ? 'no-store' : 'default',
          signal: controller ? controller.signal : undefined,
        });
      }).then(function (res) {
        if (!res.ok) throw new Error('section read failed: HTTP ' + res.status);
        return res.text();
      });
      return request.then(function (value) {
        if (timer) clearTimeout(timer);
        return value;
      }, function (error) {
        if (timer) clearTimeout(timer);
        throw error;
      });
    });
  }

  function panelDescriptor(panel, variantId) {
    return {
      panel: panel,
      rowLabel: panel.getAttribute('data-color-panel'),
      rowId: panel.getAttribute('data-row-option-value'),
      variantId: variantId || panel.getAttribute('data-anchor-variant-id') || '',
    };
  }

  function validateColorPayload(data, settings, descriptor) {
    if (!data || data.version !== 2 || String(data.product_id) !== String(settings.productId) ||
        data.row_option_index !== settings.rowOptionIndex || data.col_option_index !== settings.colOptionIndex ||
        data.row_value !== descriptor.rowLabel || String(data.row_value_id) !== String(descriptor.rowId) ||
        !Array.isArray(data.columns) || !data.columns.length) {
      throw new Error('colour response context mismatch');
    }
    var columns = new Map();
    var variantIds = new Set();
    data.columns.forEach(function (column) {
      if (!column || column.option_value_id == null || typeof column.label !== 'string') {
        throw new Error('invalid colour column');
      }
      var key = String(column.option_value_id);
      if (columns.has(key)) throw new Error('duplicate colour column');
      var v = column.variant;
      if (v !== null) {
        if (!v || !Number.isSafeInteger(v.id) || !Array.isArray(v.options) ||
            v.options[settings.rowOptionIndex - 1] !== descriptor.rowLabel ||
            v.options[settings.colOptionIndex - 1] !== column.label ||
            typeof v.available !== 'boolean' || !Number.isFinite(v.price) || v.price < 0 ||
            variantIds.has(String(v.id))) throw new Error('invalid colour variant');
        variantMax(v); // Fail closed on missing/invalid stock information.
        variantIds.add(String(v.id));
      }
      columns.set(key, column);
    });
    return columns;
  }

  function fetchColor(settings, descriptor, fresh) {
    if (!settings.matrixSectionId || !descriptor.rowId) return Promise.reject(new Error('colour context missing'));
    var url = settings.productUrl + '?section_id=' + encodeURIComponent(settings.matrixSectionId);
    if (descriptor.variantId) url += '&variant=' + encodeURIComponent(descriptor.variantId);
    else url += '&option_values=' + encodeURIComponent(descriptor.rowId);
    if (fresh) url += '&_ts=' + Date.now();
    return readSectionText(url, fresh).then(function (html) {
      // Do not parse the whole returned section into a document: its palette
      // contains image URLs. Only extract the inert JSON record.
      var match = /<script\b[^>]*\bdata-blanks-color-json\b[^>]*>([\s\S]*?)<\/script>/i.exec(html);
      if (!match) throw new Error('colour JSON missing');
      var data = JSON.parse(match[1]);
      return validateColorPayload(data, settings, descriptor);
    });
  }

  function variantMax(v) {
    if (!v || typeof v.available !== 'boolean') throw new Error('variant availability unknown');
    if (!Object.prototype.hasOwnProperty.call(v, 'inventory_management') ||
        (v.inventory_policy !== 'deny' && v.inventory_policy !== 'continue') ||
        !Number.isSafeInteger(v.inventory_quantity)) throw new Error('variant inventory data missing');
    if (v.inventory_management === 'shopify') {
      if (v.inventory_policy !== 'continue' && v.inventory_policy !== 'deny') throw new Error('inventory policy unknown');
      if (!Number.isFinite(v.inventory_quantity)) throw new Error('variant quantity unknown');
    }
    if (!v.available) return 0;
    return v.inventory_management === 'shopify' && v.inventory_policy !== 'continue'
      ? Math.max(0, v.inventory_quantity) : 9999;
  }

  function applyColorPanel(panel, columns, settings, onlyPending) {
    var adjusted = 0;
    var rows = Array.from(panel.querySelectorAll('[data-col-option-value]'));
    // Validate completeness before changing any row or quantity.
    rows.forEach(function (row) {
      var column = columns.get(row.getAttribute('data-col-option-value'));
      if (!column || column.label !== row.getAttribute('data-col-label')) throw new Error('colour column missing');
    });
    rows.forEach(function (row) {
      if (onlyPending && !row.hasAttribute('data-blanks-hydrate')) return;
      var column = columns.get(row.getAttribute('data-col-option-value'));
      var oldInput = row.querySelector('.blanks-matrix__qty');
      var before = oldInput ? parseQty(oldInput) : 0;
      fillTierRow(row, column.variant, settings, panel.getAttribute('data-color-panel'), column.label, !onlyPending);
      var after = row.querySelector('.blanks-matrix__qty');
      if (after && parseQty(after) < before) adjusted++;
    });
    return adjusted;
  }

  function hydrateColorPanel(panel, settings) {
    if (!panel.querySelector('[data-blanks-hydrate]')) return Promise.resolve();
    return fetchColor(settings, panelDescriptor(panel), false).then(function (columns) {
      applyColorPanel(panel, columns, settings, true);
    });
  }

  // Read and validate ALL selected colours before applying their snapshots.
  // This cache is never shared with the displayed/previously hydrated panels.
  function fetchOrderAvailability(root, settings, items) {
    if (!settings.colorJsonMode) {
      return fetchFreshSectionDoc(settings).then(function (doc) {
        var avail = availabilityFromDoc(doc);
        var jobs = items.filter(function (item) { return !avail.has(String(item.id)); }).map(function (item) {
          return fetchVariantMaxById(settings, item.id).then(function (max) {
            avail.set(String(item.id), max);
          });
        });
        return Promise.allSettled(jobs).then(function (results) {
          var failed = results.find(function (r) { return r.status === 'rejected'; });
          if (failed) throw failed.reason;
          return { avail: avail, doc: doc, snapshots: [] };
        });
      });
    }
    var groups = new Map();
    items.forEach(function (item) {
      var input = findQtyInput(root, String(item.id));
      var panel = input && input.closest('[data-color-panel]');
      if (!panel) throw new Error('selected variant colour unknown');
      var key = panel.getAttribute('data-row-option-value');
      if (!key) throw new Error('selected colour ID missing');
      if (!groups.has(key)) groups.set(key, { descriptor: panelDescriptor(panel, item.id), items: [] });
      groups.get(key).items.push(item);
    });
    var requests = Array.from(groups.values()).map(function (group) {
      return fetchColor(settings, group.descriptor, true).then(function (columns) {
        var byId = new Map();
        columns.forEach(function (column) {
          if (column.variant) byId.set(String(column.variant.id), column.variant);
        });
        group.items.forEach(function (item) {
          if (!byId.has(String(item.id))) throw new Error('selected variant absent from fresh colour response');
        });
        return { panel: group.descriptor.panel, columns: columns, byId: byId, items: group.items };
      });
    });
    return Promise.allSettled(requests).then(function (results) {
      var failed = results.find(function (r) { return r.status === 'rejected'; });
      if (failed) throw failed.reason;
      var snapshots = results.map(function (r) { return r.value; });
      var avail = new Map();
      snapshots.forEach(function (snapshot) {
        snapshot.items.forEach(function (item) {
          avail.set(String(item.id), variantMax(snapshot.byId.get(String(item.id))));
        });
      });
      return { avail: avail, snapshots: snapshots };
    });
  }

  function formatMoneyEUR(cents) {
    var n = Number(cents);
    if (!Number.isFinite(n)) n = 0;
    var amount = n / 100;
    try {
      return (
        new Intl.NumberFormat('fr-FR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount) + ' €'
      );
    } catch (e) {
      return amount.toFixed(2) + ' €';
    }
  }

  function parseQty(input) {
    var v = parseInt(input.value, 10);
    if (!Number.isFinite(v) || v < 0) return 0;
    return v;
  }

  function readMatrixSettings(root) {
    return {
      productUrl: root.getAttribute('data-product-url') || '',
      productId: root.getAttribute('data-product-id') || '',
      colorJsonMode: root.getAttribute('data-color-json-mode') === 'true',
      stockRefreshEnabled:
        root.getAttribute('data-stock-refresh-enabled') === 'true',
      stockRefreshUrl: root.getAttribute('data-stock-refresh-url') || '',
      matrixSectionId: root.getAttribute('data-section-id') || '',
      sectionId:
        root.getAttribute('data-variant-json-section') ||
        'blanks-matrix-variant-json',
      colOptionIndex:
        parseInt(root.getAttribute('data-col-option-index'), 10) || 1,
      rowOptionIndex:
        parseInt(root.getAttribute('data-row-option-index'), 10) || 2,
      isLoggedIn: root.getAttribute('data-is-logged-in') === 'true',
      lowStockThreshold:
        parseInt(root.getAttribute('data-low-stock-threshold'), 10) || 8,
      msgOutOfStock:
        root.getAttribute('data-msg-out-of-stock') || 'Rupture',
      msgAvailable:
        root.getAttribute('data-msg-available') || 'Disponible',
      msgInStockSuffix:
        root.getAttribute('data-msg-in-stock-suffix') || 'en stock',
      qtyAriaPrefix:
        root.getAttribute('data-qty-aria-prefix') || 'Quantité',
      msgStockAdjusted:
        root.getAttribute('data-msg-stock-adjusted') ||
        'Stock mis à jour : certaines quantités ont été réduites au stock encore disponible. Vérifiez puis validez à nouveau.',
      msgStockCheckFailed:
        root.getAttribute('data-msg-stock-check-failed') ||
        "Impossible de vérifier le stock fournisseur pour le moment. Rien n'a été ajouté au panier. Réessayez dans quelques secondes.",
      msgStockChecking:
        root.getAttribute('data-msg-stock-checking') || 'Vérification du stock…',
    };
  }

  function labelKey(colLabel, rowLabel) {
    return String(colLabel || '') + '\u0001' + String(rowLabel || '');
  }

  function parseVariantFromSectionHtml(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var script = doc.querySelector('[data-blanks-variant-json]');
    if (script && script.textContent) {
      try {
        var parsed = JSON.parse(script.textContent.trim());
        if (parsed && parsed.id != null) return parsed;
        return null;
      } catch (e) {
        /* fall through */
      }
    }

    var section = doc.querySelector('.shopify-section');
    var text = section ? section.textContent.trim() : String(html || '').trim();
    if (!text || text === 'null') return null;

    var start = text.indexOf('{');
    var end = text.lastIndexOf('}');
    if (start < 0 || end <= start) return null;
    try {
      var fallback = JSON.parse(text.slice(start, end + 1));
      if (fallback && fallback.id != null) return fallback;
      return null;
    } catch (e2) {
      return null;
    }
  }

  function buildSectionUrl(productUrl, sectionId, optionValues) {
    return (
      productUrl +
      '?section_id=' +
      encodeURIComponent(sectionId) +
      '&option_values=' +
      optionValues
    );
  }

  function fetchVariantViaSection(settings, optionValues) {
    if (!optionValues) return Promise.resolve(null);

    var url = buildSectionUrl(
      settings.productUrl,
      settings.sectionId,
      optionValues
    );

    return readSectionText(url, false)
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var script = doc.querySelector('[data-blanks-variant-json]');
        if (!script) throw new Error('variant JSON section missing');
        var variant = JSON.parse(script.textContent);
        if (variant !== null && (!variant || variant.id == null)) {
          throw new Error('variant JSON section invalid');
        }
        return variant;
      });
  }

  function fetchVariantForEntry(settings, entry) {
    return fetchVariantViaSection(settings, entry.optionValues).then(function (variant) {
      if (!variant) return null; // Valid response: this combination does not exist.
      if (!Array.isArray(variant.options) ||
          variant.options[settings.rowOptionIndex - 1] !== entry.rowLabel ||
          variant.options[settings.colOptionIndex - 1] !== entry.colLabel) {
        throw new Error('variant options do not match the requested cell');
      }
      return variant;
    });
  }

  function updateRowThumbnails(root, variantMap) {
    root.querySelectorAll('.blanks-matrix__thumb[data-row-label]').forEach(function (img) {
      var rowLabel = img.getAttribute('data-row-label');
      if (!rowLabel) return;

      variantMap.forEach(function (variant, key) {
        if (!variant || !variant.featured_image_src) return;
        var parts = key.split('\u0001');
        if (parts.length !== 2 || parts[1] !== rowLabel) return;
        img.src = variant.featured_image_src;
      });
    });
  }

  function buildStockHtml(variant, settings) {
    if (!variant) {
      return '<span class="blanks-matrix__cell--empty">—</span>';
    }

    var stockClass = 'blanks-matrix__stock-value';
    var stockHtml = '';
    var stockDotClass = '';
    var showShippingHint = false;

    if (!variant.available) {
      stockHtml = settings.msgOutOfStock;
      stockClass += ' is-out';
      stockDotClass = 'is-red';
    } else if (variant.inventory_management === 'shopify') {
      var qty = Number(variant.inventory_quantity) || 0;
      if (qty <= 0 && variant.inventory_policy === 'continue') {
        stockHtml = settings.msgAvailable;
      } else if (qty <= 0) {
        stockHtml = settings.msgOutOfStock;
        stockClass += ' is-out';
        stockDotClass = 'is-red';
      } else if (qty <= settings.lowStockThreshold) {
        stockHtml = qty + ' ' + settings.msgInStockSuffix;
        stockClass += ' is-low';
        stockDotClass = 'is-orange';
        showShippingHint = true;
      } else {
        stockHtml = qty + ' ' + settings.msgInStockSuffix;
        stockDotClass = 'is-green';
        showShippingHint = true;
      }
    } else {
      stockHtml = settings.msgAvailable;
      showShippingHint = true;
    }

    var html =
      '<div class="' +
      stockClass +
      '">' +
      (stockDotClass
        ? '<span class="blanks-matrix__stock-dot ' +
          stockDotClass +
          '" aria-hidden="true"></span>'
        : '') +
      stockHtml +
      '</div>';

    if (showShippingHint) {
      html +=
        '<div class="blanks-matrix__shipping-hint">' +
        '<span class="blanks-matrix__shipping-flash" aria-hidden="true">⚡</span>' +
        'Expedition en 24/48h' +
        '</div>';
    }

    return html;
  }

  function buildTierStockHtml(variant, settings) {
    if (!variant) {
      return '<span class="blanks-matrix__cell--empty">—</span>';
    }

    var stockClass = 'blanks-matrix__stock-value';
    var stockHtml = '';

    if (!variant.available) {
      stockHtml = settings.msgOutOfStock;
      stockClass += ' is-out';
    } else if (variant.inventory_management === 'shopify') {
      var qty = Number(variant.inventory_quantity) || 0;
      if (qty <= 0 && variant.inventory_policy === 'continue') {
        stockHtml = settings.msgAvailable;
      } else if (qty <= 0) {
        stockHtml = settings.msgOutOfStock;
        stockClass += ' is-out';
      } else {
        stockHtml = String(qty);
        if (qty <= settings.lowStockThreshold) {
          stockClass += ' is-low';
        }
      }
    } else {
      stockHtml = settings.msgAvailable;
    }

    return '<span class="' + stockClass + '">' + stockHtml + '</span>';
  }

  function buildTierPriceHtml(variant, multiplier, settings) {
    if (!variant) {
      return '<span class="blanks-matrix__cell--empty">—</span>';
    }

    if (!settings.isLoggedIn) {
      var lockedExamples = {
        100: '4,94 €',
        95: '4,69 €',
        85: '4,20 €',
        80: '3,95 €'
      };
      return (
        '<span class="blanks-matrix__tier-price is-locked" aria-label="Prix masqué"><span aria-hidden="true">' +
        (lockedExamples[multiplier] || '4,94 €') +
        '</span></span>'
      );
    }

    var cents = Math.floor((Number(variant.price) || 0) * multiplier / 100);
    return (
      '<span class="blanks-matrix__tier-price' +
      (multiplier < 100 ? ' is-discount' : '') +
      '">' +
      formatMoneyEUR(cents) +
      '</span>'
    );
  }

  function buildTierQtyInputHtml(variant, settings, rowLabel, colLabel) {
    var disabled = !variant || !variant.available || !settings.isLoggedIn;
    var maxQ = 9999;

    if (
      variant &&
      variant.inventory_management === 'shopify' &&
      variant.inventory_policy !== 'continue'
    ) {
      maxQ = Number(variant.inventory_quantity) || 0;
      if (maxQ < 0) maxQ = 0;
    }

    var aria =
      settings.qtyAriaPrefix + ' ' + rowLabel + ' — ' + colLabel;

    return (
      '<input class="blanks-matrix__qty blanks-matrix__tier-qty" type="number" min="0" value="0"' +
      (disabled ? ' disabled' : ' max="' + maxQ + '"') +
      (!disabled && variant ? ' data-variant-id="' + variant.id + '"' : '') +
      (!disabled && variant ? ' data-price-cents="' + variant.price + '"' : '') +
      ' aria-label="' +
      aria.replace(/"/g, '&quot;') +
      '" inputmode="numeric">'
    );
  }

  function buildQtyControlHtml(variant, settings, rowLabel, colLabel, disabled) {
    var aria =
      settings.qtyAriaPrefix + ' ' + rowLabel + ' — ' + colLabel;
    var maxQ = 9999;
    var variantId = '';
    var priceCents = '';

    if (variant && !disabled) {
      variantId = String(variant.id);
      priceCents = String(variant.price);
      if (
        variant.inventory_management === 'shopify' &&
        variant.inventory_policy !== 'continue'
      ) {
        maxQ = Number(variant.inventory_quantity) || 0;
        if (maxQ < 0) maxQ = 0;
      }
    }

    var disabledAttr = disabled ? ' disabled tabindex="-1"' : '';
    var maxAttr = disabled ? '' : ' max="' + maxQ + '"';
    var variantAttr = variantId
      ? ' data-variant-id="' + variantId + '"'
      : '';
    var priceAttr = priceCents
      ? ' data-price-cents="' + priceCents + '"'
      : '';

    return (
      '<div class="blanks-matrix__qty-wrap' +
      (disabled ? ' is-disabled' : '') +
      '">' +
      '<button type="button" class="blanks-matrix__qty-btn" data-qty-decr' +
      disabledAttr +
      ' aria-label="Diminuer la quantité">' +
      '<span class="blanks-matrix__qty-icon" aria-hidden="true">' +
      '<svg viewBox="0 0 20 20" focusable="false" role="presentation">' +
      '<path d="M4.5 10a.75.75 0 0 1 .75-.75h9.5a.75.75 0 0 1 0 1.5h-9.5A.75.75 0 0 1 4.5 10Z"></path>' +
      '</svg></span></button>' +
      '<input class="blanks-matrix__qty" type="number" min="0" value="0"' +
      maxAttr +
      (disabled ? ' disabled' : '') +
      variantAttr +
      priceAttr +
      ' aria-label="' +
      aria.replace(/"/g, '&quot;') +
      '" inputmode="numeric">' +
      '<button type="button" class="blanks-matrix__qty-btn" data-qty-incr' +
      disabledAttr +
      ' aria-label="Augmenter la quantité">' +
      '<span class="blanks-matrix__qty-icon" aria-hidden="true">' +
      '<svg viewBox="0 0 20 20" focusable="false" role="presentation">' +
      '<path d="M10 4.5a.75.75 0 0 1 .75.75v4h4a.75.75 0 0 1 0 1.5h-4v4a.75.75 0 0 1-1.5 0v-4h-4a.75.75 0 0 1 0-1.5h4v-4A.75.75 0 0 1 10 4.5Z"></path>' +
      '</svg></span></button></div>'
    );
  }

  function fillTierRow(row, variant, settings, rowLabel, colLabel, preserveQuantity) {
    if (!row) return;

    if (!variant || !variant.available) {
      row.classList.add('is-unavailable');
    } else {
      row.classList.remove('is-unavailable');
    }

    row.querySelectorAll('[data-tier-price]').forEach(function (cell) {
      var multiplier = parseInt(cell.getAttribute('data-tier-price'), 10) || 100;
      cell.innerHTML = buildTierPriceHtml(variant, multiplier, settings);
    });

    var stockCell = row.querySelector('[data-tier-stock]');
    if (stockCell) {
      stockCell.innerHTML = buildTierStockHtml(variant, settings);
    }

    var qtyCell = row.querySelector('[data-tier-qty]');
    if (qtyCell) {
      var oldInput = qtyCell.querySelector('.blanks-matrix__qty');
      if (preserveQuantity && oldInput) {
        var previous = parseQty(oldInput);
        var disabled = !variant || !variant.available || !settings.isLoggedIn;
        var max = variant ? variantMax(variant) : 0;
        oldInput.disabled = disabled;
        if (disabled) {
          oldInput.removeAttribute('max');
          oldInput.removeAttribute('data-variant-id');
          oldInput.removeAttribute('data-price-cents');
        } else {
          oldInput.setAttribute('max', String(max));
          oldInput.setAttribute('data-variant-id', String(variant.id));
          oldInput.setAttribute('data-price-cents', String(variant.price));
        }
        oldInput.value = String(disabled ? 0 : Math.min(previous, max));
      } else {
        qtyCell.innerHTML = buildTierQtyInputHtml(variant, settings, rowLabel, colLabel);
      }
    }

    if (variant) row.setAttribute('data-variant-key', String(variant.id));
    else row.removeAttribute('data-variant-key');
    row.removeAttribute('data-blanks-hydrate');
  }

  function buildBuyHtml(variant, settings, rowLabel, colLabel) {
    if (!variant) {
      return '<span class="blanks-matrix__cell--empty">—</span>';
    }

    var priceHtml = settings.isLoggedIn
      ? '<div class="blanks-matrix__price">' +
        formatMoneyEUR(variant.price) +
        '</div>'
      : '<div class="blanks-matrix__price is-locked" aria-label="Prix masqué"><span aria-hidden="true">4,94 €</span></div>';

    var disabled =
      !variant.available || !settings.isLoggedIn;

    return (
      priceHtml +
      buildQtyControlHtml(
        variant,
        settings,
        rowLabel,
        colLabel,
        disabled
      )
    );
  }

  function fillHydratedCells(entry, variant, settings) {
    if (entry.rowEl) {
      fillTierRow(
        entry.rowEl,
        variant,
        settings,
        entry.rowLabel,
        entry.colLabel
      );
    }
    if (entry.stockTd) {
      entry.stockTd.innerHTML = buildStockHtml(variant, settings);
      entry.stockTd.removeAttribute('data-blanks-hydrate');
    }
    if (entry.buyTd) {
      entry.buyTd.innerHTML = buildBuyHtml(
        variant,
        settings,
        entry.rowLabel,
        entry.colLabel
      );
      entry.buyTd.removeAttribute('data-blanks-hydrate');
    }
  }

  function hydrateMissingCells(root, settings) {
    var cells = root.querySelectorAll('[data-blanks-hydrate]');
    if (!cells.length || !settings.productUrl) {
      return Promise.resolve(new Map());
    }

    var pairs = new Map();
    cells.forEach(function (td) {
      var key = td.getAttribute('data-option-values');
      if (!key) return;
      if (!pairs.has(key)) {
        pairs.set(key, {
          optionValues: key,
          rowLabel: td.getAttribute('data-row-label') || '',
          colLabel: td.getAttribute('data-col-label') || '',
          stockTd: null,
          buyTd: null,
          rowEl: null,
          resolved: false,
        });
      }
      var rec = pairs.get(key);
      var kind = td.getAttribute('data-blanks-hydrate');
      if (kind === 'stock') rec.stockTd = td;
      if (kind === 'buy') rec.buyTd = td;
      if (kind === 'tier-row') rec.rowEl = td;
    });

    var entries = Array.from(pairs.values());
    if (!entries.length) return Promise.resolve(new Map());

    var variantMap = new Map();

    var index = 0;
    var failed = 0;
    function fetchNext() {
      if (index >= entries.length) return Promise.resolve();
      var entry = entries[index++];
      return fetchVariantForEntry(settings, entry)
        .then(function (variant) {
          fillHydratedCells(entry, variant, settings);
          if (variant) variantMap.set(labelKey(entry.colLabel, entry.rowLabel), variant);
        })
        .catch(function () {
          // Keep the pending marker so the user can retry a failed request.
          failed++;
        })
        .then(fetchNext);
    }
    var workers = [];
    for (var i = 0; i < Math.min(3, entries.length); i++) workers.push(fetchNext());
    return Promise.all(workers).then(function () {
      if (failed) throw new Error('Some variant requests failed');
      return variantMap;
    });
  }


  function bindQtyInputs(root, onChange) {
    root.querySelectorAll('.blanks-matrix__qty').forEach(function (input) {
      if (input.getAttribute('data-qty-bound') === '1') return;
      input.setAttribute('data-qty-bound', '1');
      input.addEventListener('input', function () {
        var max = parseInt(input.getAttribute('max'), 10);
        if (Number.isFinite(max) && parseQty(input) > max) {
          input.value = String(max);
        }
        onChange();
      });
      input.addEventListener('change', onChange);
    });
  }

  // ── Supplier stock verification (Imbretex → Shopify) ─────────────────────
  // Client-side we only remember UNTIL WHEN the last successful server check is
  // valid (server TTL, clock-skew corrected, 2 s margin). Quantities are never
  // cached here: they are always re-read from Shopify before add-to-cart.
  function createStockVerifier(settings) {
    var OVERALL_MS = 30000;
    var state = { promise: null, freshUntil: 0 };
    var enabled = !!(
      settings.stockRefreshEnabled &&
      settings.stockRefreshUrl &&
      settings.productId
    );

    function postOnce(signal) {
      return fetch(settings.stockRefreshUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId: String(settings.productId) }),
        credentials: 'omit',
        signal: signal,
      }).then(function (res) {
        return res
          .json()
          .catch(function () {
            return {};
          })
          .then(function (body) {
            body = body || {};
            body.httpStatus = res.status;
            return body;
          });
      });
    }

    function run() {
      var started = Date.now();
      var controller =
        typeof window.AbortController === 'function'
          ? new window.AbortController()
          : null;
      var timer = window.setTimeout(function () {
        if (controller) controller.abort();
      }, OVERALL_MS);

      function attempt() {
        return postOnce(controller ? controller.signal : undefined).then(
          function (body) {
            if (body.ok === true) {
              var serverNow = Number(body.server_now_ms);
              var skew = serverNow ? Date.now() - serverNow : 0;
              var exp = Number(body.expires_at_ms);
              state.freshUntil = exp ? exp + skew - 2000 : 0;
              return body;
            }
            // Feature switched off server-side: no supplier check, the caller
            // still validates against current Shopify stock.
            if (body.status === 'disabled') return body;

            var retryable =
              body.status === 'in_progress' || body.status === 'busy';
            if (retryable && Date.now() - started < OVERALL_MS - 3000) {
              var wait = Math.min(Number(body.retry_after_ms) || 1500, 3000);
              return new Promise(function (r) {
                window.setTimeout(r, wait);
              }).then(attempt);
            }
            var err = new Error(body.error || body.status || 'stock check failed');
            err.status = body.status || 'http_' + body.httpStatus;
            throw err;
          }
        );
      }

      return attempt().then(
        function (v) {
          window.clearTimeout(timer);
          return v;
        },
        function (e) {
          window.clearTimeout(timer);
          throw e;
        }
      );
    }

    return {
      enabled: enabled,
      isFresh: function () {
        return Date.now() < state.freshUntil;
      },
      // Returns the in-flight promise if any (no duplicate request), resolves
      // immediately if the last successful check is still valid, otherwise
      // starts a new check.
      ensure: function () {
        if (!enabled) return Promise.resolve({ status: 'not_applicable' });
        if (state.promise) return state.promise;
        if (Date.now() < state.freshUntil) {
          return Promise.resolve({ ok: true, status: 'fresh_local' });
        }
        state.promise = run().then(
          function (v) {
            state.promise = null;
            return v;
          },
          function (e) {
            state.promise = null;
            throw e;
          }
        );
        return state.promise;
      },
    };
  }

  // Fresh server render of this section (Liquid sees Shopify inventory; the
  // storefront product.js does not expose inventory_quantity). Strict: throws.
  function fetchFreshSectionDoc(settings) {
    if (!settings.matrixSectionId || !settings.productUrl) {
      return Promise.reject(new Error('section not configured'));
    }
    var url =
      settings.productUrl +
      '?section_id=' +
      encodeURIComponent(settings.matrixSectionId) +
      '&_ts=' +
      Date.now();
    return readSectionText(url, true)
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        if (!doc.querySelector('.blanks-matrix')) {
          throw new Error('section render empty');
        }
        return doc;
      });
  }

  // Map variantId -> max orderable quantity from a fresh render. A row whose
  // input is disabled (sold out / unavailable) maps to 0.
  function availabilityFromDoc(doc) {
    var map = new Map();
    doc.querySelectorAll('[data-variant-key]').forEach(function (row) {
      var key = row.getAttribute('data-variant-key');
      var input = row.querySelector('.blanks-matrix__qty');
      if (!key) return;
      if (!input || input.disabled) {
        map.set(key, 0);
        return;
      }
      var n = parseInt(input.getAttribute('max'), 10);
      map.set(key, Number.isFinite(n) ? Math.max(0, n) : 0);
    });
    return map;
  }

  // Variants not rendered by Liquid (products above the Liquid variant limit
  // are hydrated client-side): read them one by one through the variant JSON
  // section. Strict: unknown availability rejects, ATC is not performed.
  function fetchVariantMaxById(settings, variantId) {
    var url =
      settings.productUrl +
      '?variant=' +
      encodeURIComponent(variantId) +
      '&section_id=' +
      encodeURIComponent(settings.sectionId) +
      '&_ts=' +
      Date.now();
    return readSectionText(url, true)
      .then(function (html) {
        var v = parseVariantFromSectionHtml(html);
        if (!v || String(v.id) !== String(variantId)) {
          throw new Error('variant availability unknown');
        }
        if (!v.available) return 0;
        if (
          v.inventory_management === 'shopify' &&
          v.inventory_policy !== 'continue'
        ) {
          var q = Number(v.inventory_quantity);
          if (!Number.isFinite(q)) throw new Error('variant quantity unknown');
          return Math.max(0, q);
        }
        return 9999;
      });
  }

  // Current cart quantities by variant id. Strict: throws on failure.
  function fetchCartQuantities() {
    var url =
      (window.theme && theme.routes && theme.routes.cart_url
        ? theme.routes.cart_url
        : '/cart') + '.js';

    return fetch(url, { credentials: 'same-origin', cache: 'no-store' })
      .then(function (res) {
        if (!res.ok) throw new Error('cart.js failed');
        return res.json();
      })
      .then(function (cart) {
        var map = new Map();
        (cart && cart.items ? cart.items : []).forEach(function (line) {
          if (line && line.id != null) {
            var key = String(line.id);
            map.set(key, (map.get(key) || 0) + (Number(line.quantity) || 0));
          }
        });
        return map;
      });
  }

  // Patch stock labels, availability and max values from a fresh render while
  // keeping the customer's quantities (clamped to the new max). The focused
  // input is not replaced, only updated, so typing is not interrupted.
  // Returns the number of quantities that had to be reduced.
  function applyFreshSection(root, doc) {
    var fresh = doc.querySelector('.blanks-matrix');
    var adjusted = 0;
    if (!fresh) return 0;

    root.querySelectorAll('[data-variant-key]').forEach(function (row) {
      var key = row.getAttribute('data-variant-key');
      var nrow = fresh.querySelector('[data-variant-key="' + key + '"]');
      if (!nrow) return;

      row.classList.toggle(
        'is-unavailable',
        nrow.classList.contains('is-unavailable')
      );

      var oldStock = row.querySelector('[data-variant-stock]');
      var newStock = nrow.querySelector('[data-variant-stock]');
      if (oldStock && newStock) oldStock.innerHTML = newStock.innerHTML;

      var oldCell = row.querySelector('[data-variant-qty]');
      var newCell = nrow.querySelector('[data-variant-qty]');
      if (!oldCell || !newCell) return;

      var oldInput = oldCell.querySelector('.blanks-matrix__qty');
      var newInput = newCell.querySelector('.blanks-matrix__qty');
      var prev = oldInput ? parseQty(oldInput) : 0;
      var enabled = newInput && !newInput.disabled;
      var max = enabled ? parseInt(newInput.getAttribute('max'), 10) : 0;
      if (!Number.isFinite(max)) max = 0;
      var nextVal = enabled ? Math.min(prev, max) : 0;
      if (nextVal < prev) adjusted += 1;

      if (
        oldInput &&
        enabled &&
        !oldInput.disabled &&
        document.activeElement === oldInput
      ) {
        oldInput.setAttribute('max', String(max));
        oldInput.value = String(nextVal);
        return;
      }

      oldCell.innerHTML = newCell.innerHTML;
      var placed = oldCell.querySelector('.blanks-matrix__qty');
      if (placed && !placed.disabled) placed.value = String(nextVal);
    });

    return adjusted;
  }

  function findQtyInput(root, variantId) {
    var row = root.querySelector('[data-variant-key="' + variantId + '"]');
    if (row) return row.querySelector('.blanks-matrix__qty');
    return root.querySelector('input[data-variant-id="' + variantId + '"]');
  }

  function initSection(root, opts) {
    opts = opts || {};
    if (!root || root.getAttribute('data-blanks-matrix-init') === '1') return;
    root.setAttribute('data-blanks-matrix-init', '1');

    var qtyInputs = root.querySelectorAll('.blanks-matrix__qty');
    var totalEls = root.querySelectorAll('[data-blanks-matrix-total]');
    var submitBtn = root.querySelector('[data-blanks-matrix-submit]');
    var submitLabel = root.querySelector('[data-submit-label]');
    var errorEl = root.querySelector('[data-blanks-matrix-error]');
    var galleryModal = root.querySelector('[data-row-gallery-modal]');
    var galleryImage = root.querySelector('[data-gallery-image]');
    var galleryCaption = root.querySelector('[data-gallery-caption]');
    var galleryPrev = root.querySelector('[data-gallery-prev]');
    var galleryNext = root.querySelector('[data-gallery-next]');
    var galleryItems = [];
    var galleryIndex = 0;
    var matrixSettings = readMatrixSettings(root);
    // Single source of truth for "an add-to-cart is in progress". Every code
    // path that toggles the submit button must respect it (updateTotal is
    // called by input handlers, background patches and the ATC flow itself).
    var isSubmitting = false;

    function refreshQtyInputs() {
      qtyInputs = root.querySelectorAll('.blanks-matrix__qty');
    }

    function setError(msg) {
      if (!errorEl) return;
      if (msg) {
        errorEl.textContent = msg;
        errorEl.classList.add('is-visible');
      } else {
        errorEl.textContent = '';
        errorEl.classList.remove('is-visible');
      }
    }

    function computeTotalCents() {
      refreshQtyInputs();
      var sum = 0;
      qtyInputs.forEach(function (input) {
        if (input.disabled) return;
        var cents = parseInt(input.getAttribute('data-price-cents'), 10) || 0;
        sum += parseQty(input) * cents;
      });
      return sum;
    }

    function updateTotal() {
      refreshQtyInputs();
      totalEls = root.querySelectorAll('[data-blanks-matrix-total]');
      var formattedTotal = formatMoneyEUR(computeTotalCents());
      totalEls.forEach(function (totalEl) {
        totalEl.textContent = formattedTotal;
      });
      var any =
        Array.prototype.some.call(qtyInputs, function (inp) {
          return !inp.disabled && parseQty(inp) > 0;
        });
      if (submitBtn) submitBtn.disabled = isSubmitting || !any;
    }

    function updateHeaderCartCount() {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('cart.js failed');
          return res.json();
        })
        .then(function (cart) {
          var count = String((cart && cart.item_count) || 0);
          document.querySelectorAll('.thb-item-count').forEach(function (el) {
            el.textContent = count;
          });
        })
        .catch(function () {
          // Do not block UX if cart count refresh fails.
        });
    }

    function showAddedFeedback() {
      if (!submitBtn || !submitLabel) return;
      if (!submitBtn.dataset.defaultLabel) {
        submitBtn.dataset.defaultLabel = submitLabel.textContent || '';
      }
      submitBtn.classList.add('is-success');
      submitLabel.textContent = 'Ajoute au panier';
      window.setTimeout(function () {
        submitBtn.classList.remove('is-success');
        submitLabel.textContent =
          submitBtn.dataset.defaultLabel || 'Ajouter a mon panier';
      }, 1800);
    }

    function renderGallery() {
      if (!galleryItems.length || !galleryImage) return;
      var item = galleryItems[galleryIndex] || galleryItems[0];
      galleryImage.src = item.src || '';
      galleryImage.alt = item.alt || '';
      if (galleryCaption) {
        galleryCaption.textContent = item.alt || '';
      }
      var showNav = galleryItems.length > 1;
      if (galleryPrev) galleryPrev.hidden = !showNav;
      if (galleryNext) galleryNext.hidden = !showNav;
    }

    function closeGallery() {
      if (!galleryModal) return;
      galleryModal.setAttribute('hidden', 'hidden');
      document.body.classList.remove('blanks-gallery-open');
      galleryItems = [];
      galleryIndex = 0;
      if (galleryImage) {
        galleryImage.src = '';
        galleryImage.alt = '';
      }
    }

    function openGallery(items) {
      if (!galleryModal || !items || !items.length) return;
      galleryItems = items;
      galleryIndex = 0;
      renderGallery();
      galleryModal.removeAttribute('hidden');
      document.body.classList.add('blanks-gallery-open');
    }

    bindQtyInputs(root, function () {
      setError('');
      updateTotal();
    });

    function ensureColorPanel(panel) {
      if (!panel) return Promise.resolve();
      if (panel._blanksHydration) return panel._blanksHydration;
      if (!panel.querySelector('table')) {
        var template = root.querySelector('[data-blanks-color-table]');
        if (!template) return Promise.resolve();
        panel.appendChild(template.content.cloneNode(true));
        panel.querySelectorAll('[data-blanks-hydrate]').forEach(function (row) {
          row.setAttribute('data-row-label', panel.getAttribute('data-color-panel'));
          row.setAttribute('data-option-values',
            row.getAttribute('data-col-option-value') + ',' + panel.getAttribute('data-row-option-value'));
        });
      }
      var oldRetry = panel.querySelector('[data-blanks-retry]');
      if (oldRetry) oldRetry.remove();
      panel.setAttribute('aria-busy', 'true');
      var hydrate = matrixSettings.colorJsonMode ? hydrateColorPanel : hydrateMissingCells;
      panel._blanksHydration = hydrate(panel, matrixSettings)
        .catch(function () {
          var retry = document.createElement('button');
          retry.type = 'button';
          retry.setAttribute('data-blanks-retry', '');
          retry.className = 'blanks-matrix__retry';
          retry.textContent = 'Chargement incomplet. Réessayer';
          panel.appendChild(retry);
        })
        .then(function () {
          panel.removeAttribute('aria-busy');
          panel._blanksHydration = null;
          rebindAfterPatch();
        });
      return panel._blanksHydration;
    }

    var initialPanel = root.querySelector('[data-color-panel].is-active');
    if (initialPanel) ensureColorPanel(initialPanel);
    else hydrateMissingCells(root, matrixSettings).then(rebindAfterPatch).catch(function () {});

    var verifier = createStockVerifier(matrixSettings);

    function rebindAfterPatch() {
      bindQtyInputs(root, function () {
        setError('');
        updateTotal();
      });
      updateTotal();
    }

    // Background check after a real interaction with THIS matrix. Logged-out
    // visitors cannot order (inputs disabled) and never trigger it.
    var lastTriggerAt = 0;
    function onMatrixInteraction(e) {
      if (e && !e.target.closest('.blanks-matrix__qty, [data-qty-incr], [data-qty-decr]')) return;
      if (!verifier.enabled || !matrixSettings.isLoggedIn) return;
      if (e && e.isTrusted === false) return;
      if (verifier.isFresh()) return;
      var now = Date.now();
      if (now - lastTriggerAt < 5000) return;
      lastTriggerAt = now;
      verifier
        .ensure()
        .then(function (result) {
          if (!result || result.ok !== true || isSubmitting) return;
          if (matrixSettings.colorJsonMode) {
            var panel = root.querySelector('[data-color-panel].is-active');
            if (!panel) return;
            return fetchColor(matrixSettings, panelDescriptor(panel), true).then(function (columns) {
              if (isSubmitting) return;
              var adjusted = applyColorPanel(panel, columns, matrixSettings, false);
              rebindAfterPatch();
              if (adjusted > 0) setError(matrixSettings.msgStockAdjusted);
            });
          }
          return fetchFreshSectionDoc(matrixSettings).then(function (doc) {
            if (isSubmitting) return;
            var adjusted = applyFreshSection(root, doc);
            rebindAfterPatch();
            if (adjusted > 0) setError(matrixSettings.msgStockAdjusted);
          });
        })
        .catch(function (err) {
          // Background only: the add-to-cart path re-checks and blocks.
          if (window.console) console.warn('[blanks-matrix] stock check', err);
        });
    }
    ['pointerdown', 'keydown', 'focusin'].forEach(function (evt) {
      root.addEventListener(evt, onMatrixInteraction, { passive: true });
    });

    root.addEventListener('click', function (e) {
      var retry = e.target.closest('[data-blanks-retry]');
      if (retry && root.contains(retry)) {
        ensureColorPanel(retry.closest('[data-color-panel]'));
        return;
      }
      var colorTrigger = e.target.closest('[data-color-trigger]');
      if (colorTrigger && root.contains(colorTrigger)) {
        var colorValue = colorTrigger.getAttribute('data-color-trigger') || '';
        root.querySelectorAll('[data-color-trigger]').forEach(function (button) {
          var active = button === colorTrigger;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        root.querySelectorAll('[data-color-panel]').forEach(function (panel) {
          var active = panel.getAttribute('data-color-panel') === colorValue;
          panel.classList.toggle('is-active', active);
          if (active) {
            panel.removeAttribute('hidden');
            ensureColorPanel(panel);
          } else {
            panel.setAttribute('hidden', 'hidden');
          }
        });
        var selected = root.querySelector('[data-blanks-selected-color]');
        if (selected) selected.textContent = colorValue;
        return;
      }

      var thumb = e.target.closest('[data-row-gallery]');
      if (thumb && root.contains(thumb)) {
        var raw = thumb.getAttribute('data-row-gallery') || '[]';
        var parsed = raw
          .split('||')
          .map(function (chunk) {
            if (!chunk) return null;
            var parts = chunk.split('~~');
            return {
              src: parts[0] || '',
              alt: parts.slice(1).join('~~') || '',
            };
          })
          .filter(Boolean);
        if (Array.isArray(parsed) && parsed.length) {
          openGallery(parsed);
        }
        return;
      }

      var btn = e.target.closest('[data-qty-decr], [data-qty-incr]');
      if (!btn || !root.contains(btn) || btn.disabled) return;
      var wrap = btn.closest('.blanks-matrix__qty-wrap');
      if (!wrap) return;
      var input = wrap.querySelector('.blanks-matrix__qty');
      if (!input || input.disabled) return;
      var max = parseInt(input.getAttribute('max'), 10);
      var q = parseQty(input);
      if (btn.hasAttribute('data-qty-decr')) {
        q = Math.max(0, q - 1);
      } else {
        q = q + 1;
        if (Number.isFinite(max) && q > max) q = max;
      }
      input.value = String(q);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    if (galleryModal) {
      galleryModal.addEventListener('click', function (e) {
        if (e.target.closest('[data-gallery-close]')) {
          closeGallery();
        }
      });
      if (galleryPrev) {
        galleryPrev.addEventListener('click', function () {
          if (!galleryItems.length) return;
          galleryIndex =
            (galleryIndex - 1 + galleryItems.length) % galleryItems.length;
          renderGallery();
        });
      }
      if (galleryNext) {
        galleryNext.addEventListener('click', function () {
          if (!galleryItems.length) return;
          galleryIndex = (galleryIndex + 1) % galleryItems.length;
          renderGallery();
        });
      }
      document.addEventListener('keydown', function (e) {
        if (!galleryModal || galleryModal.hasAttribute('hidden')) return;
        if (e.key === 'Escape') closeGallery();
        if (e.key === 'ArrowLeft' && galleryItems.length > 1) {
          galleryIndex =
            (galleryIndex - 1 + galleryItems.length) % galleryItems.length;
          renderGallery();
        }
        if (e.key === 'ArrowRight' && galleryItems.length > 1) {
          galleryIndex = (galleryIndex + 1) % galleryItems.length;
          renderGallery();
        }
      });
    }

    updateTotal();

    if (!submitBtn) return;

    function addItemsToCart(cartItems) {
      var url =
        (window.theme && theme.routes && theme.routes.cart_add_url) ||
        '/cart/add.js';
      var payload = { items: cartItems };

      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      })
        .then(function (res) {
          return res.json().then(function (body) {
            return { ok: res.ok, status: res.status, body: body };
          });
        })
        .then(function (result) {
          if (!result.ok) {
            var desc = result.body && result.body.description;
            if (desc && typeof desc === 'object') {
              desc =
                desc.base ||
                (Array.isArray(desc)
                  ? desc.join(', ')
                  : JSON.stringify(desc));
            }
            var msg =
              (typeof desc === 'string' && desc) ||
              (result.body && result.body.message) ||
              "Impossible d'ajouter au panier.";
            throw new Error(msg);
          }

          document.dispatchEvent(new CustomEvent('cart:refresh'));

          if (typeof window.dispatchCustomEvent === 'function') {
            window.dispatchCustomEvent('cart:item-added', {
              product: result.body,
            });
          }

          updateHeaderCartCount();
          showAddedFeedback();

          var mode = root.getAttribute('data-after-add') || 'stay';
          if (mode === 'cart') {
            window.location.href =
              (window.theme && theme.routes && theme.routes.cart_url) ||
              '/cart';
            return;
          }
          if (
            mode === 'drawer' &&
            window.theme &&
            theme.settings &&
            theme.settings.cart_drawer
          ) {
            document.body.classList.add('open-cc');
            var drawer = document.getElementById('Cart-Drawer');
            if (drawer) drawer.classList.add('active');
            if (typeof window.dispatchCustomEvent === 'function') {
              window.dispatchCustomEvent('cart-drawer:open');
            }
          }

          qtyInputs.forEach(function (inp) {
            if (!inp.disabled) inp.value = '0';
          });
          updateTotal();
        });
    }

    submitBtn.addEventListener('click', function () {
      if (isSubmitting) return;
      setError('');
      refreshQtyInputs();
      var items = [];

      qtyInputs.forEach(function (input) {
        if (input.disabled) return;
        var q = parseQty(input);
        if (q <= 0) return;
        var id = input.getAttribute('data-variant-id');
        if (!id) return;
        items.push({ id: Number(id), quantity: q });
      });

      if (!items.length) {
        setError(
          root.getAttribute('data-msg-select-qty') ||
            'Indiquez au moins une quantité.'
        );
        return;
      }

      isSubmitting = true;
      submitBtn.classList.add('is-loading');
      submitBtn.disabled = true;
      if (submitLabel && !submitBtn.dataset.defaultLabel) {
        submitBtn.dataset.defaultLabel = submitLabel.textContent || '';
      }

      function done() {
        isSubmitting = false;
        submitBtn.classList.remove('is-loading');
        if (submitLabel && submitBtn.dataset.defaultLabel) {
          submitLabel.textContent = submitBtn.dataset.defaultLabel;
        }
        updateTotal();
      }

      // Non-Imbretex products: unchanged behaviour (Shopify enforces stock).
      if (!verifier.enabled) {
        addItemsToCart(items)
          .catch(function (err) {
            setError(err.message || 'Erreur réseau.');
          })
          .then(done);
        return;
      }

      if (submitLabel && !verifier.isFresh()) {
        submitLabel.textContent = matrixSettings.msgStockChecking;
      }

      // 1) supplier check: reuse in-flight / still-valid check, else new one;
      // 2) re-read Shopify stock + cart; 3) clamp or add. Any failure → no ATC.
      verifier
        .ensure()
        .then(function (result) {
          // status 'disabled' = operator switched the feature off server-side.
          // This is NOT a successful supplier check: we fall back to the
          // pre-feature behaviour (current Shopify stock, still re-read and
          // clamped below; Shopify itself enforces the deny policy).
          if (result && result.status === 'disabled' && window.console) {
            console.info('[blanks-matrix] supplier stock check disabled; using Shopify stock only');
          }
          return Promise.all([
            fetchOrderAvailability(root, matrixSettings, items),
            fetchCartQuantities(),
          ]);
        })
        .then(function (results) {
          var availability = results[0];
          if (availability.doc) applyFreshSection(root, availability.doc);
          availability.snapshots.forEach(function (snapshot) {
            applyColorPanel(snapshot.panel, snapshot.columns, matrixSettings, false);
          });
          rebindAfterPatch();
          return { avail: availability.avail, cartMap: results[1] };
        })
        .then(function (ctx) {
          var adjusted = 0;
          items.forEach(function (item) {
            var key = String(item.id);
            var stock = ctx.avail.get(key) || 0;
            var remaining = Math.max(0, stock - (ctx.cartMap.get(key) || 0));
            var allowed = Math.min(item.quantity, remaining);
            if (allowed < item.quantity) {
              adjusted += 1;
              var live = findQtyInput(root, key);
              if (live && !live.disabled) live.value = String(allowed);
            }
          });

          if (adjusted > 0) {
            refreshQtyInputs();
            updateTotal();
            setError(matrixSettings.msgStockAdjusted);
            done();
            return;
          }

          if (submitLabel && submitBtn.dataset.defaultLabel) {
            submitLabel.textContent = submitBtn.dataset.defaultLabel;
          }
          return addItemsToCart(items)
            .catch(function (err) {
              setError(err.message || 'Erreur réseau.');
            })
            .then(done);
        })
        .catch(function (err) {
          if (window.console) console.warn('[blanks-matrix] ATC blocked', err);
          setError(matrixSettings.msgStockCheckFailed);
          done();
        });
    });
  }

  function boot() {
    document.querySelectorAll('.blanks-matrix').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    if (ev.target && ev.target.querySelector) {
      var sec = ev.target.querySelector('.blanks-matrix');
      if (sec) initSection(sec);
    }
  });
})();